import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  ListSubheader,
  Button,
  TextField,
  InputAdornment,
  Icon,
  Typography,
} from '@mui/material';
import { StoreUtil } from 'doctivity-shared/utils';
import { loadAnalytics } from 'store/actions/analyticsActions';

// The analytics store is keyed by report type, and only one Market Data tab is mounted
// at a time, so every tab can share this one slot.
const OPTIONS_TYPE = 'CLAIMS_ORGANIZATION_FILTER_OPTIONS';

const styles = (theme) => ({
  filterDropDown: {
    width: 200,
    marginRight: theme.spacing(),
    [theme.breakpoints.down('md')]: {
      minWidth: '100%',
    },
  },
  select: {
    // Org lists run long, so give the menu roughly double MUI's default height on
    // desktop. Capped against the viewport so it can't outgrow a short screen, and
    // left at the shorter height on small screens where 80vh is already the limit.
    maxHeight: 420,
    // A FIXED width, not a minimum. Without it the menu is sized by its content, so it
    // resized on every keystroke as the longest visible name changed -- the list
    // appeared to jump around while you typed.
    maxWidth: 'calc(100vw - 32px)',
    [theme.breakpoints.up('md')]: {
      maxHeight: 'min(840px, 80vh)',
      // Wider than the 200px input on purpose; at the input's width almost every
      // organization name would be clipped.
      width: 420,
    },
    '& .MuiMenuItem-root.Mui-selected, .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: '#00a887',
      color: 'white',
    },
  },
  stickySelectedCount: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 2,
    borderBottom: '1px solid black',
    fontSize: 14,
    fontWeight: 500,
    color: '#000',
    lineHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'default',
    // ListSubheader inside a Select menu is inert by default; the Clear action needs
    // to receive its own clicks.
    pointerEvents: 'auto',
  },
  clearButton: {
    padding: '0 6px',
    minWidth: 0,
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'none',
    lineHeight: '20px',
  },
  stickySearch: {
    position: 'sticky',
    top: 36,
    backgroundColor: '#fff',
    zIndex: 2,
    padding: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
  },
  organizationItem: {
    fontSize: 14,
    // Truncate rather than wrap: a wrapped long name costs two or three rows of the
    // list. The full name is on the element's title attribute for hover.
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // Follows the menu's fixed width instead of setting one of its own, so a long name
    // truncates rather than widening the menu.
    maxWidth: '100%',
  },
  emptyItem: {
    fontSize: 13,
    fontStyle: 'italic',
    color: '#666',
  },
});

/**
 * "By Organization" filter for the Market Data (/claims) tabs.
 *
 * The option list is NOT the full organization table -- it is the distinct set of
 * organizations present in the current tab's own result set, fetched from the
 * CLAIMS_ORGANIZATION_FILTER_OPTIONS report with the tab's live criteria. So changing
 * the market, service line, patient type, specialty or payors re-narrows the list.
 *
 * `value` is an array of organization ids; an EMPTY array means "All". Selecting "All"
 * clears every other selection, matching the Service Line multi-select.
 */
class OrganizationSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      open: false,
      search: '',
    };
  }

  componentDidMount() {
    this.fetchOptions();
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(this.props.optionsFilter) !==
      JSON.stringify(prevProps.optionsFilter)
    ) {
      this.fetchOptions();
    }
  }

  fetchOptions() {
    const { dispatch, optionsFilter } = this.props;

    // Same scope guard the backend applies. Note the .id checks: an unresolved market
    // arrives as { id: undefined }, which is truthy but scopes nothing.
    if (!this.hasScope()) {
      return;
    }

    dispatch(
      loadAnalytics({
        type: OPTIONS_TYPE,
        filter: optionsFilter,
        opts: { limit: 1000 },
      })
    );
  }

  hasScope() {
    const { optionsFilter } = this.props;
    return !!(
      optionsFilter &&
      (optionsFilter.market?.id || optionsFilter.tag?.id)
    );
  }

  getOptions() {
    const { organizationOptions } = this.props;
    if (!StoreUtil.isLoaded(organizationOptions)) {
      return [];
    }
    return organizationOptions?.data?.rows ?? [];
  }

  getSelectedIds() {
    const { value } = this.props;
    if (!Array.isArray(value)) {
      return value ? [parseInt(value, 10)] : [];
    }
    return value
      .map((v) => parseInt(v, 10))
      .filter((v) => Number.isInteger(v) && v > 0);
  }

  renderLabel(selectedIds) {
    if (selectedIds.length === 0) {
      return 'All';
    }
    if (selectedIds.length === 1) {
      const match = this.getOptions().find((o) => o.id === selectedIds[0]);
      return match ? match.name : '1 organization';
    }
    return `${selectedIds.length} organizations`;
  }

  render() {
    const { classes, organizationOptions } = this.props;
    const { search } = this.state;

    const options = this.getOptions();
    const selectedIds = this.getSelectedIds();
    const selectedSet = new Set(selectedIds);
    const isLoading = this.hasScope() && !StoreUtil.isLoaded(organizationOptions);

    const term = search.trim().toLowerCase();
    // Selected organizations stay in the list even when they don't match the search,
    // otherwise MUI has no MenuItem for part of `value` and you can't unselect them.
    const visible = term
      ? options.filter(
          (o) =>
            selectedSet.has(o.id) || (o.name || '').toLowerCase().includes(term)
        )
      : options;

    const menuItems = [];
    menuItems.push(
      <MenuItem value='all' key='all'>
        <em>All</em>
      </MenuItem>
    );

    if (isLoading) {
      menuItems.push(
        <MenuItem key='loading' disabled className={classes.emptyItem}>
          Loading organizations...
        </MenuItem>
      );
    } else if (options.length === 0) {
      menuItems.push(
        <MenuItem key='empty' disabled className={classes.emptyItem}>
          No organizations in these results
        </MenuItem>
      );
    } else if (visible.length === 0) {
      menuItems.push(
        <MenuItem key='no-match' disabled className={classes.emptyItem}>
          No matches for &quot;{search.trim()}&quot;
        </MenuItem>
      );
    }

    visible.forEach((organization) => {
      menuItems.push(
        <MenuItem
          value={organization.id}
          key={organization.id}
          className={classes.organizationItem}
          title={organization.name}
        >
          {organization.name}
        </MenuItem>
      );
    });

    return (
      <FormControl
        variant='outlined'
        className={classes.filterDropDown}
        key='filter-organization'
      >
        <InputLabel>By Organization</InputLabel>
        <Select
          multiple
          value={selectedIds.length > 0 ? selectedIds : ['all']}
          onChange={this.onOrganizationChange}
          label='By Organization'
          open={this.state.open}
          onOpen={this.onOpen}
          onClose={this.onClose}
          renderValue={() => (
            <Typography align='left' noWrap>
              {this.renderLabel(selectedIds)}
            </Typography>
          )}
          MenuProps={{
            PaperProps: {
              className: classes.select,
            },
          }}
        >
          <ListSubheader
            key='selected-count'
            className={classes.stickySelectedCount}
          >
            <span>{selectedIds.length} selected</span>
            {selectedIds.length > 0 && (
              <Button
                size='small'
                color='primary'
                className={classes.clearButton}
                // onMouseDown, not onClick: the Select closes the menu on mouse down,
                // so an onClick handler here never fires.
                onMouseDown={this.onClear}
                disableRipple
              >
                Clear
              </Button>
            )}
          </ListSubheader>
          <ListSubheader key='search' className={classes.stickySearch}>
            <TextField
              fullWidth
              size='small'
              variant='outlined'
              placeholder='Search organizations'
              value={search}
              autoComplete='off'
              onChange={this.onSearchChange}
              // The Select menu treats typing as type-ahead navigation and would steal
              // every keystroke (and jump focus) out of this field.
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Icon fontSize='small'>search</Icon>
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>
          {menuItems}
        </Select>
      </FormControl>
    );
  }

  onOpen = () => {
    this.setState({ open: true });
  };

  onClose = () => {
    this.setState({ open: false, search: '' });
  };

  // Back to All without scrolling to the top of the list. Deliberately leaves the menu
  // open so the user can immediately pick a different set.
  onClear = (event) => {
    event.stopPropagation();
    event.preventDefault();
    this.props.onChange([]);
  };

  onSearchChange = (event) => {
    this.setState({ search: event.target.value });
  };

  onOrganizationChange = (event) => {
    const raw = event.target.value;
    let ids = Array.isArray(raw) ? raw : [raw];

    // Clearing the last chip is the same as asking for All.
    if (ids.length === 0) {
      this.props.onChange([]);
      return;
    }

    if (ids.includes('all')) {
      // MUI appends the newly clicked value, so "all" last means the user just
      // clicked All -- drop everything else. Otherwise All was the standing
      // selection and picking a real organization replaces it.
      if (ids[ids.length - 1] === 'all') {
        this.props.onChange([]);
        return;
      }
      ids = ids.filter((id) => id !== 'all');
    }

    this.props.onChange(
      ids.map((id) => parseInt(id, 10)).filter((id) => Number.isInteger(id))
    );
  };
}

OrganizationSelect.propTypes = {
  classes: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  // The analytics filter describing the CURRENT tab -- must include `source`
  // ('claims' | 'shared_affiliation' | 'payors'), `claims_type`, and the tab's market
  // or tag. It must NOT include organization_ids.
  optionsFilter: PropTypes.object,
  organizationOptions: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    organizationOptions: StoreUtil.get(state.analytics, OPTIONS_TYPE),
  };
}

const styled = withStyles(styles)(OrganizationSelect);
const connected = connect(mapStateToProps)(styled);
export { connected as OrganizationSelect };
